import React from 'react';
import { IconChevron } from '../../assets/icons/IconChevron';
import type { RelanceAgendaEntry } from '../../types/customerFollowUp/relanceAgenda';
import { RelanceExpectedTag } from './RelanceExpectedTag';
import { RelanceRankTag } from './RelanceRankTag';

interface RelanceForecastRowProps {
    entry: RelanceAgendaEntry;
    /** Absent for a row with nothing to open (an expected relance that cannot be collected). */
    onOpen?: () => void;
}

const CELLS_CLASS = 'col-span-full grid grid-cols-subgrid items-center gap-x-xl pl-xs pr-0.5 py-m text-left text-s text-content-subtle';

export const RelanceForecastRow: React.FC<RelanceForecastRowProps> = ({ entry, onOpen }) => {
    const { item } = entry;
    const expected = entry.status === 'expected';
    const prefix = expected ? 'expected-row' : 'deactivated-row';

    const cells = (
        <>
            <span className="min-w-0 truncate font-medium text-content-secondary">{item.title}</span>
            <span className="flex items-center gap-xs">
                <RelanceRankTag rank={item.relanceCount + 1} postponed={!expected} variant="outlined" testId={`${prefix}-tag-${item.id}`} className="transition-colors duration-300 ease-out group-hover:text-content-secondary" />
                {expected && <RelanceExpectedTag testId={`${prefix}-expected-tag-${item.id}`} />}
            </span>
            <span className="flex items-center justify-end gap-s">
                <span className="font-semibold tabular-nums whitespace-nowrap text-content-secondary">{item.amount}</span>
                {onOpen ? <IconChevron className="size-3.5 shrink-0" /> : <span className="size-3.5 shrink-0" aria-hidden="true" />}
            </span>
        </>
    );

    return (
        <li data-testid={`${prefix}-${item.id}`} data-status={entry.status} className="col-span-full grid grid-cols-subgrid border-b border-line-subtle">
            {!onOpen ? (
                <div data-testid={`${prefix}-content-${item.id}`} className={CELLS_CLASS}>{cells}</div>
            ) : (
                <button
                    type="button"
                    data-testid={`${prefix}-btn-${item.id}`}
                    onClick={onOpen}
                    className={`group ${CELLS_CLASS} cursor-pointer transition-colors duration-300 ease-out hover:bg-overlay-weak hover:text-content-secondary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-focus-ring`}
                >
                    {cells}
                </button>
            )}
        </li>
    );
};
